import type { Schemas } from "@10twenty/api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router";
import { api, ApiError, errorMessage, unwrap } from "../api";
import { useAuth } from "../auth/AuthContext";
import { useCapabilities } from "../chat/useChatAccount";
import {
  freeLimitText,
  groupCircles,
  KIND_DESCRIPTIONS,
  KIND_LABELS,
  memberCountText,
  parseInviteInput,
} from "../circles/circles";
import { chooseCircle } from "../circles/choice";
import { EventForm } from "../components/EventForm";
import { askError, RequestState } from "../components/MySupervision";
import {
  deletionText,
  eventCreateBody,
  eventStatus,
  localTimeZone,
  newEventDraft,
  scheduleText,
  STATUS_LABELS,
  type EventDraft,
} from "../events/events";
import { actionMode, myRequestText, recentRequests, supervisionPoll } from "../supervision/member";
import { useAsk, useMyRequests } from "../supervision/useMyRequests";
import { useNow } from "../useNow";

/** Circle names, as in the contract. */
const NAME_MAX = 80;
/** Moves events between upcoming, live and ended this often. */
const TICK_MS = 30_000;

type Circle = Schemas["Circle"];
type FreeLimits = Schemas["Capabilities"]["free_member_limits"];
type Mode = Schemas["SupervisionMode"];
type Request = Schemas["SupervisionRequest"];

const ASKS_FIRST =
  "Your supervision asks a guardian first. It's created once a guardian approves in each circle that asks for it.";
const TOLD = "Your guardians are told.";

export function CirclesPage() {
  const circles = useQuery({
    queryKey: ["circles"],
    queryFn: async () => unwrap(await api.GET("/circles")),
    refetchInterval: (query) => supervisionPoll(query.state.data),
  });
  const limits = useCapabilities().data?.free_member_limits;
  const nowMs = useNow(TICK_MS);
  const [viewerZone] = useState(localTimeZone);
  // Joining or creating may need a guardian's approval (or tell them).
  const createMode = actionMode("create_circle", circles.data ?? []);
  const requests = useMyRequests((circles.data ?? []).some((circle) => circle.supervision));
  const shown = recentRequests(requests.data ?? [], ["create_circle", "join_circle"], nowMs);
  return (
    <div className="stack">
      <h1>Circles</h1>
      {circles.isPending && <p className="muted">Loading your circles…</p>}
      {circles.error && <p className="error">{errorMessage(circles.error)}</p>}
      {circles.data?.length === 0 && <p className="muted">You aren't in a circle yet.</p>}
      {circles.data &&
        groupCircles(circles.data, nowMs).map((group) => (
          <section key={group.kind} className="stack circle-group" aria-labelledby={`circles-${group.kind}`}>
            <h2 id={`circles-${group.kind}`} className="circle-group-title">
              {group.title}
            </h2>
            <ul className="circle-list">
              {group.circles.map((circle) => (
                <CircleItem key={circle.id} circle={circle} nowMs={nowMs} viewerZone={viewerZone} />
              ))}
            </ul>
          </section>
        ))}
      <CreateCircleCard
        limits={limits}
        mode={createMode}
        requests={shown.filter((r) => r.action === "create_circle" && r.params.circle?.kind !== "event")}
      />
      <CreateEventCard
        limit={limits?.event}
        viewerZone={viewerZone}
        mode={createMode}
        requests={shown.filter((r) => r.action === "create_circle" && r.params.circle?.kind === "event")}
      />
      <JoinCircleCard requests={shown.filter((r) => r.action === "join_circle")} />
    </div>
  );
}

function CircleItem({ circle, nowMs, viewerZone }: { circle: Circle; nowMs: number; viewerZone: string }) {
  const event = circle.event;
  const status = event ? eventStatus(event, nowMs) : null;
  return (
    <li>
      <Link to={`/circles/${circle.id}`} className={`circle-list-item${status === "ended" ? " ended" : ""}`}>
        <span className="circle-list-name">
          {circle.name}
          {status && <span className={`event-badge ${status}`}>{STATUS_LABELS[status]}</span>}
        </span>
        {event && (
          <span className="muted">
            {scheduleText(event, viewerZone)}
            {status === "ended" && ` · ${deletionText(event, viewerZone)}`}
          </span>
        )}
        <span className="muted">
          {memberCountText(circle)}
          {circle.role === "admin" && " · Admin"}
          {circle.supervision ? " · Supervised" : circle.supervision_offer && " · Supervision offered"}
        </span>
      </Link>
    </li>
  );
}

function CreateCircleCard({
  limits,
  mode,
  requests,
}: {
  limits: FreeLimits | undefined;
  mode: Mode;
  requests: Request[];
}) {
  const { session } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [kind, setKind] = useState<"family" | "friends">("family");
  const create = useMutation({
    mutationFn: async (body: { name: string; kind: "family" | "friends" }) =>
      unwrap(await api.POST("/circles", { body })),
    onSuccess: async (circle) => {
      if (session) chooseCircle(session.userId, circle.id);
      await queryClient.invalidateQueries({ queryKey: ["circles"] });
    },
    // A supervision started or tightened since the page loaded: the form asks instead.
    onError: async (error) => {
      if (error instanceof ApiError && error.code === "supervised")
        await queryClient.invalidateQueries({ queryKey: ["circles"] });
    },
  });

  const ask = useAsk();

  function submit(event: FormEvent) {
    event.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    if (mode === "ask") {
      ask.mutate(
        { action: "create_circle", params: { circle: { name: trimmed, kind } } },
        { onSuccess: () => setName("") },
      );
      return;
    }
    // Navigation is per call, so it only happens if this form is still on screen.
    create.mutate({ name: trimmed, kind }, { onSuccess: (circle) => void navigate(`/circles/${circle.id}`) });
  }

  return (
    <section className="card">
      <form onSubmit={submit} className="stack">
        <h2>New circle</h2>
        <p className="muted">
          You'll be its admin, and can invite people with a link or QR code.
          {mode === "ask" && ` ${ASKS_FIRST}`}
          {mode === "notify" && ` ${TOLD}`}
        </p>
        {create.error && <p className="error">{createError(create.error)}</p>}
        {ask.error && <p className="error">{askError(ask.error)}</p>}
        {requests.map((request) => (
          <RequestState key={request.id} request={request} label={myRequestText(request)} />
        ))}
        <fieldset className="kind-choice">
          <legend>Who is it for?</legend>
          {(["family", "friends"] as const).map((option) => (
            <label key={option} className="kind-option">
              <input
                type="radio"
                name="circle-kind"
                value={option}
                checked={kind === option}
                onChange={() => setKind(option)}
              />
              <span>
                <strong>{KIND_LABELS[option]}</strong>
                <span className="hint">
                  {KIND_DESCRIPTIONS[option]}
                  {freeLimitText(limits?.[option]) && ` ${freeLimitText(limits?.[option])}.`}
                </span>
              </span>
            </label>
          ))}
        </fieldset>
        <label>
          Name
          <input
            required
            maxLength={NAME_MAX}
            placeholder={kind === "family" ? "Family" : "Friends"}
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </label>
        <button type="submit" disabled={create.isPending || ask.isPending || !name.trim()}>
          {mode === "ask" ? "Ask to create circle" : "Create circle"}
        </button>
      </form>
    </section>
  );
}

function createError(error: unknown): string {
  if (error instanceof ApiError && error.code === "supervised")
    return "Creating a circle needs a guardian's approval now. Send the form again to ask them.";
  return errorMessage(error);
}

function createEventError(error: unknown): string {
  if (error instanceof ApiError && error.code === "event_limit")
    return "You're already organising as many events as you can. Wait until one ends, or delete one.";
  return createError(error);
}

function CreateEventCard({
  limit,
  viewerZone,
  mode,
  requests,
}: {
  limit: number | null | undefined;
  viewerZone: string;
  mode: Mode;
  requests: Request[];
}) {
  const { session } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  // The form's starting values, set when it opens (a fresh default start each time).
  const [initial, setInitial] = useState<EventDraft | null>(null);
  const create = useMutation({
    mutationFn: async (draft: EventDraft) => unwrap(await api.POST("/circles", { body: eventCreateBody(draft) })),
    onSuccess: async (circle) => {
      if (session) chooseCircle(session.userId, circle.id);
      await queryClient.invalidateQueries({ queryKey: ["circles"] });
    },
    onError: async (error) => {
      if (error instanceof ApiError && error.code === "supervised")
        await queryClient.invalidateQueries({ queryKey: ["circles"] });
    },
  });
  const ask = useAsk();
  const free = freeLimitText(limit);

  return (
    <section className="card stack">
      <h2>New event</h2>
      <p className="muted">
        A temporary circle for a day out, a party or a trip: members see each other's locations only while the event is
        on, and it is deleted with its chat 30 days after it ends.{free && ` ${free}.`}
        {mode === "ask" && ` ${ASKS_FIRST}`}
        {mode === "notify" && ` ${TOLD}`}
      </p>
      {requests.map((request) => (
        <RequestState key={request.id} request={request} label={myRequestText(request)} />
      ))}
      {initial ? (
        <EventForm
          initial={initial}
          submitLabel={mode === "ask" ? "Ask to create event" : "Create event"}
          busy={create.isPending || ask.isPending}
          error={create.error ? createEventError(create.error) : ask.error ? askError(ask.error) : null}
          onSubmit={(draft) =>
            mode === "ask"
              ? ask.mutate(
                  { action: "create_circle", params: { circle: eventCreateBody(draft) } },
                  { onSuccess: () => setInitial(null) },
                )
              : create.mutate(draft, { onSuccess: (circle) => void navigate(`/circles/${circle.id}`) })
          }
          onCancel={() => {
            create.reset();
            ask.reset();
            setInitial(null);
          }}
        />
      ) : (
        <button type="button" className="secondary" onClick={() => setInitial(newEventDraft(Date.now(), viewerZone))}>
          Plan an event
        </button>
      )}
    </section>
  );
}

function JoinCircleCard({ requests }: { requests: Request[] }) {
  const navigate = useNavigate();
  const [input, setInput] = useState("");
  const [problem, setProblem] = useState<string | null>(null);

  function submit(event: FormEvent) {
    event.preventDefault();
    const invite = parseInviteInput(input, window.location.origin);
    if (invite === null) {
      setProblem("That isn't a valid invite code or link.");
    } else if (invite.origin !== null && invite.origin !== window.location.origin) {
      setProblem(`That invite is for another server (${invite.origin}). Open the link itself to join there.`);
    } else {
      setProblem(null);
      void navigate(`/join/${invite.code}`);
    }
  }

  return (
    <section className="card">
      <form onSubmit={submit} className="stack">
        <h2>Join a circle or event</h2>
        <p className="muted">Paste an invite link, or type the 8-character code.</p>
        {requests.map((request) => (
          <RequestState key={request.id} request={request} label={myRequestText(request)} />
        ))}
        {problem && (
          <p className="error" role="alert">
            {problem}
          </p>
        )}
        <label>
          Invite code or link
          <input
            required
            autoCapitalize="characters"
            autoComplete="off"
            spellCheck={false}
            value={input}
            onChange={(event) => setInput(event.target.value)}
          />
        </label>
        <button type="submit" className="secondary" disabled={!input.trim()}>
          Continue
        </button>
      </form>
    </section>
  );
}
